<template>
  <div class="rich-text-editor-wrapper">
    <!-- Toolbar -->
    <div class="rich-editor-toolbar" v-if="!disabled">
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('bold')"
        :class="{ active: isFormatActive('bold') }"
        title="Bold"
      >
        <i class="fa fa-bold"></i>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('italic')"
        :class="{ active: isFormatActive('italic') }"
        title="Italic"
      >
        <i class="fa fa-italic"></i>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('underline')"
        :class="{ active: isFormatActive('underline') }"
        title="Underline"
      >
        <i class="fa fa-underline"></i>
      </button>

      <div class="toolbar-separator"></div>

      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('insertUnorderedList')"
        title="Bullet List"
      >
        <i class="fa fa-list-ul"></i>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('insertOrderedList')"
        title="Numbered List"
      >
        <i class="fa fa-list-ol"></i>
      </button>

      <div class="toolbar-separator"></div>

      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('justifyLeft')"
        title="Align Left"
      >
        <i class="fa fa-align-left"></i>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('justifyCenter')"
        title="Align Center"
      >
        <i class="fa fa-align-center"></i>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        @click="formatText('justifyRight')"
        title="Align Right"
      >
        <i class="fa fa-align-right"></i>
      </button>
    </div>

    <!-- Editor -->
    <div
      ref="richEditor"
      class="rich-text-editor form-control"
      :contenteditable="!disabled"
      @input="updateContent"
      @paste="handlePaste"
      @keydown="handleKeydown"
      :placeholder="placeholder"
      :class="{ 'disabled-editor': disabled }"
    ></div>

    <!-- Character count -->
    <div class="field-hint" v-if="showCharCount">
      {{ currentCharacterCount }}/{{ maxLength }} characters
    </div>
  </div>
</template>

<script>
export default {
  name: "RichTextEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Enter your message content...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 2000,
    },
    showCharCount: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentCharacterCount: 0,
    };
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.$refs.richEditor && this.$refs.richEditor.innerHTML !== newValue) {
          this.$refs.richEditor.innerHTML = newValue || "";
          this.updateCharacterCount();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.$refs.richEditor) {
      this.$refs.richEditor.innerHTML = this.value || "";
      this.updateCharacterCount();
    }
  },
  methods: {
    formatText(command) {
      document.execCommand(command, false, null);
      this.updateContent();
      this.$refs.richEditor.focus();
    },
    isFormatActive(command) {
      return document.queryCommandState(command);
    },
    updateContent() {
      if (this.$refs.richEditor) {
        const content = this.$refs.richEditor.innerHTML;
        this.updateCharacterCount();
        this.$emit("input", content);
      }
    },
    updateCharacterCount() {
      if (this.$refs.richEditor) {
        this.currentCharacterCount = this.getPlainTextFromHTML(this.$refs.richEditor.innerHTML).length;
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData(
        "text/plain"
      );
      document.execCommand("insertText", false, text);
      this.updateContent();
    },
    handleKeydown(event) {
      // Allow all input - validation will be done at the step level
    },
    getPlainTextLength() {
      if (!this.$refs.richEditor) return 0;
      return this.getPlainTextFromHTML(this.$refs.richEditor.innerHTML).length;
    },
    getPlainTextFromHTML(html) {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return div.textContent || div.innerText || "";
    },
    placeCursorAtEnd() {
      if (this.$refs.richEditor) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(this.$refs.richEditor);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
  },
};
</script>

<style scoped>
.rich-text-editor-wrapper {
  width: 100%;
}

.rich-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
  overflow-x: auto;
  flex-wrap: wrap;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  background: transparent;
  color: #495057;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 14px;
  flex-shrink: 0;
  position: relative;
}

.toolbar-btn:hover {
  background: #e9ecef;
  color: #212529;
  border-color: #dee2e6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.toolbar-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.toolbar-btn.active {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border-color: #0056b3;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.toolbar-btn.active:hover {
  background: linear-gradient(135deg, #0069d9 0%, #004494 100%);
  transform: translateY(-1px);
}

.toolbar-separator {
  width: 1px;
  height: 20px;
  background: linear-gradient(180deg, transparent 0%, #dee2e6 20%, #dee2e6 80%, transparent 100%);
  margin: 0 6px;
  flex-shrink: 0;
}

.rich-text-editor {
  min-height: 140px;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px !important;
  border-radius: 0 0 6px 6px !important;
  border-top: none !important;
  border-width: 2px !important;
  border-style: solid !important;
  border-color: #e9ecef !important;
  line-height: 1.65;
  font-family: inherit;
  font-size: 14px;
  color: #212529;
  word-wrap: break-word;
  overflow-wrap: break-word;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: #ffffff;
}

.rich-text-editor:empty:before {
  content: attr(placeholder);
  color: #adb5bd;
  font-style: italic;
  font-weight: 400;
}

.rich-text-editor:focus {
  outline: none;
  border-color: #007bff !important;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
}

.rich-text-editor.disabled-editor {
  background: #e9ecef;
  cursor: not-allowed;
  opacity: 0.75;
  color: #6c757d;
}

.rich-text-editor.disabled-editor:focus {
  border-color: #e9ecef !important;
  box-shadow: none !important;
}

.rich-text-editor ul,
.rich-text-editor ol {
  margin: 10px 0;
  padding-left: 24px;
}

.rich-text-editor ul {
  list-style-type: disc;
}

.rich-text-editor ol {
  list-style-type: decimal;
}

.rich-text-editor li {
  margin: 5px 0;
  line-height: 1.6;
}

.rich-text-editor p {
  margin: 10px 0;
}

.rich-text-editor p:first-child {
  margin-top: 0;
}

.rich-text-editor p:last-child {
  margin-bottom: 0;
}

.rich-text-editor strong {
  font-weight: 600;
  color: #212529;
}

.rich-text-editor em {
  font-style: italic;
}

.rich-text-editor u {
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-offset: 2px;
}

.field-hint {
  margin-top: 6px;
  padding: 0 2px;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  text-align: right;
  letter-spacing: 0.2px;
}

/* Custom scrollbar for better aesthetics */
.rich-text-editor::-webkit-scrollbar {
  width: 8px;
}

.rich-text-editor::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 0 0 6px 0;
}

.rich-text-editor::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.rich-text-editor::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Smooth focus transition */
.rich-text-editor-wrapper {
  transition: all 0.2s ease;
}

@media (max-width: 768px) {
  .rich-editor-toolbar {
    padding: 4px 6px;
    gap: 1px;
  }

  .toolbar-btn {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }

  .toolbar-separator {
    margin: 0 4px;
  }

  .rich-text-editor {
    min-height: 120px;
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 10px 12px !important;
  }

  .field-hint {
    font-size: 11px;
  }
}

/* Improve button visual feedback */
@media (hover: hover) {
  .toolbar-btn:hover {
    transition: all 0.1s ease;
  }
}
</style>
